// src/components/dashboards/composition/smartText/smartTextModel.ts
//
// The smart text block's shape, as the dashboard tile and its panel read it
// (plan 2026-10-16-a-smart-text-block, S5; ADR-0426). Pure: no React, no
// requests.
//
// THE CLIENT ONLY SUBSTITUTES. Every string a variable shows ("€24,444",
// "+6.3%", "Last month") is written by the server
// (`services/composition/smart_text.py::resolve_variables`) and arrives with
// the single-item refresh. Nothing here formats a number, computes a change or
// names a period. What lives here is the bookkeeping around those strings:
// which variables a block declares, which tokens its text uses, and whether a
// resolved value still belongs to the definition on screen.
//
// The variable shapes mirror the backend's pydantic models (`ValueVariable`,
// `RatioVariable`, `DeltaVariable`, `PeriodVariable`); the server re-parses
// them with `extra="forbid"` on every write, so this file is a reader and a
// draft builder, never the rule.
import type { ResolvedSmartTextVariable, SmartTextStatus } from "@verity/report-components";

export type SmartTextLanguage = "en" | "nl";
export const SMART_TEXT_LANGUAGES: readonly SmartTextLanguage[] = ["en", "nl"];

export type CompareMode = "previous_period" | "same_period_last_year";
export type DeltaAs = "absolute" | "percent";
export type PeriodStyle = "name" | "range" | "name_and_range";
export type PeriodCase = "sentence" | "lower";

export type ValueVariable = { kind: "value"; metric_key: string };
export type RatioVariable = { kind: "ratio"; metric_key: string; of_metric_key: string };
export type DeltaVariable = { kind: "delta"; metric_key: string; compare: CompareMode; as: DeltaAs };
export type PeriodVariable = { kind: "period"; style: PeriodStyle; case: PeriodCase };
export type SmartTextVariable = ValueVariable | RatioVariable | DeltaVariable | PeriodVariable;
export type SmartTextVariableKind = SmartTextVariable["kind"];
export type SmartTextVariables = Record<string, SmartTextVariable>;

export const SMART_TEXT_VARIABLE_KINDS: readonly SmartTextVariableKind[] = ["value", "ratio", "delta", "period"];

/** `services/composition/smart_text.py::MAX_VARIABLES`. */
export const MAX_SMART_TEXT_VARIABLES = 12;

/** A variable's name: `VARIABLE_NAME_RE` in the backend. */
export const VARIABLE_NAME_RE = /^[a-z][a-z0-9_]{0,31}$/;

/** A token in the text: the SAME pattern `packages/report-components/src/
 *  textBlock.ts` substitutes with (`TOKEN`). The editor parses chips with it
 *  and the draft checks with it, so the editor can never recognise a token the
 *  renderer would leave as literal text, or the reverse. Global: use
 *  `matchAll`, never `exec` on a shared instance. */
export const SMART_TEXT_TOKEN_RE = /\{\{([a-z][a-z0-9_]{0,31})\}\}/g;

const COMPARE_MODES: readonly CompareMode[] = ["previous_period", "same_period_last_year"];
const DELTA_AS: readonly DeltaAs[] = ["absolute", "percent"];
const PERIOD_STYLES: readonly PeriodStyle[] = ["name", "range", "name_and_range"];
const PERIOD_CASES: readonly PeriodCase[] = ["sentence", "lower"];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/** One stored variable, with the backend's defaults filled in (`as` absolute,
 *  `style` name, `case` sentence), so two spellings of one definition compare
 *  equal. Null for anything the backend would refuse. */
export function normaliseVariable(raw: unknown): SmartTextVariable | null {
  const v = asRecord(raw);
  if (!v) return null;
  const metric = typeof v.metric_key === "string" && v.metric_key !== "" ? v.metric_key : null;
  switch (v.kind) {
    case "value":
      return metric ? { kind: "value", metric_key: metric } : null;
    case "ratio":
      return metric && typeof v.of_metric_key === "string" && v.of_metric_key !== ""
        ? { kind: "ratio", metric_key: metric, of_metric_key: v.of_metric_key }
        : null;
    case "delta":
      return metric
        ? {
            kind: "delta",
            metric_key: metric,
            compare: pick(v.compare, COMPARE_MODES, "previous_period"),
            as: pick(v.as, DELTA_AS, "absolute"),
          }
        : null;
    case "period":
      return {
        kind: "period",
        style: pick(v.style, PERIOD_STYLES, "name"),
        case: pick(v.case, PERIOD_CASES, "sentence"),
      };
    default:
      return null;
  }
}

/** The variables a spec declares, normalised; malformed entries are left out. */
export function variablesOf(spec: Record<string, unknown> | null | undefined): SmartTextVariables {
  const raw = asRecord(spec?.variables);
  const out: SmartTextVariables = {};
  if (!raw) return out;
  for (const [name, value] of Object.entries(raw)) {
    if (!VARIABLE_NAME_RE.test(name)) continue;
    const variable = normaliseVariable(value);
    if (variable) out[name] = variable;
  }
  return out;
}

export function contentOf(spec: Record<string, unknown> | null | undefined): string {
  return typeof spec?.content === "string" ? spec.content : "";
}

export function languageOf(spec: Record<string, unknown> | null | undefined): SmartTextLanguage {
  return pick(spec?.language, SMART_TEXT_LANGUAGES, "en");
}

/** The stored chart type of a smart text tile (`SMART_TEXT_CHART_TYPE`). */
export function isSmartTextSpec(spec: Record<string, unknown> | null | undefined): boolean {
  return spec?.chart_type === "smart_text";
}

export function isSmartTextItem(item: { chart_spec_json?: unknown } | null | undefined): boolean {
  return isSmartTextSpec(asRecord(item?.chart_spec_json));
}

/** Do two definitions mean the same thing? Field by field over the
 *  normalised shape: key order and absent defaults do not count. */
export function sameDefinition(a: SmartTextVariable | null | undefined, b: SmartTextVariable | null | undefined): boolean {
  if (!a || !b) return false;
  const na = normaliseVariable(a);
  const nb = normaliseVariable(b);
  if (!na || !nb) return false;
  const ka = Object.keys(na).sort();
  const kb = Object.keys(nb).sort();
  if (ka.join("|") !== kb.join("|")) return false;
  return ka.every(
    (key) => (na as Record<string, unknown>)[key] === (nb as Record<string, unknown>)[key],
  );
}

export function sameVariables(a: SmartTextVariables, b: SmartTextVariables): boolean {
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  return ka.join("|") === kb.join("|") && ka.every((name) => sameDefinition(a[name], b[name]));
}

/** The metric keys the variables read, in first-use order: what the server
 *  puts in the block's query (`variable_metric_keys`). */
export function metricKeysOf(variables: SmartTextVariables): string[] {
  const out: string[] = [];
  for (const variable of Object.values(variables)) {
    for (const key of [
      "metric_key" in variable ? variable.metric_key : null,
      variable.kind === "ratio" ? variable.of_metric_key : null,
    ]) {
      if (key && !out.includes(key)) out.push(key);
    }
  }
  return out;
}

/** The token names in a text, in order, repeats kept. */
export function tokenNames(content: string): string[] {
  return Array.from(content.matchAll(SMART_TEXT_TOKEN_RE), (m) => m[1]);
}

// ---------------------------------------------------------------------------
// The server's template rule, mirrored (`smart_text.py::parse_template`):
// any `{{…}}` whose inside is not a variable name is refused, and so is a
// token inside code or a link address. The renderer's TOKEN above is stricter
// (it ignores both), so without this the draft would call `{{ cm2 }}` fine
// and every autosave pause would end in a 422.
// ---------------------------------------------------------------------------

/** `_TOKEN_RE`: `{{…}}` with no brace or newline inside. */
const SERVER_TOKEN_RE = /\{\{([^{}\n]*)\}\}/g;

/** `_code_and_url_spans`: fenced code, inline code spans, link destinations
 *  `](…)` and autolinks `<…>`, as [start, end) offsets. */
function codeAndUrlSpans(text: string): [number, number][] {
  const spans: [number, number][] = [];
  for (const m of text.matchAll(/^[ \t]*(`{3,}|~{3,})[\s\S]*?(?:^[ \t]*\1[ \t]*$|(?![\s\S]))/gm)) {
    spans.push([m.index ?? 0, (m.index ?? 0) + m[0].length]);
  }
  let i = 0;
  while (i < text.length) {
    if (spans.some(([s, e]) => s <= i && i < e)) {
      i += 1;
      continue;
    }
    if (text[i] === "`") {
      const run = (text.slice(i).match(/^`+/)?.[0] ?? "`").length;
      const close = text.indexOf("`".repeat(run), i + run);
      if (close !== -1) {
        spans.push([i, close + run]);
        i = close + run;
        continue;
      }
      i += run;
      continue;
    }
    i += 1;
  }
  for (const m of text.matchAll(/\]\(([^)]*)\)/g)) {
    const start = (m.index ?? 0) + 2;
    spans.push([start, start + m[1].length]);
  }
  for (const m of text.matchAll(/<[^<>\s]+>/g)) {
    spans.push([m.index ?? 0, (m.index ?? 0) + m[0].length]);
  }
  return spans;
}

/** What `parse_template` would refuse in `content`: the `{{…}}` whose inside
 *  is not a name (as written), and whether some token stands in code or a
 *  link address. */
export function templateProblems(content: string): { malformed: string[]; misplaced: boolean } {
  const spans = codeAndUrlSpans(content);
  const malformed: string[] = [];
  let misplaced = false;
  for (const m of content.matchAll(SERVER_TOKEN_RE)) {
    const at = m.index ?? 0;
    if (spans.some(([s, e]) => s <= at && at < e)) misplaced = true;
    else if (!VARIABLE_NAME_RE.test(m[1])) malformed.push(m[0]);
  }
  return { malformed, misplaced };
}

export type SmartTextDraftIssues = {
  /** `{{…}}` the server refuses: its inside is not a variable name. */
  malformedTokens: string[];
  /** A token inside code or a link address (the server refuses it). */
  misplacedTokens: boolean;
  /** Tokens in the text that name no declared variable. */
  unknownTokens: string[];
  /** Declared variables the text does not use. */
  unused: string[];
  /** No variable reads a metric (the server refuses such a block). */
  noMetric: boolean;
  /** The text is empty. */
  empty: boolean;
  tooLong: boolean;
};

/** What the server's write rule would refuse, measured on the draft, so the
 *  panel can say it while the author is still typing. Soft: the draft keeps
 *  every issue; only the save waits for none. The server stays the rule
 *  (`validate_smart_text_spec`). */
export function draftIssues(content: string, variables: SmartTextVariables, maxLength: number): SmartTextDraftIssues {
  const used = new Set(tokenNames(content));
  const problems = templateProblems(content);
  return {
    malformedTokens: problems.malformed,
    misplacedTokens: problems.misplaced,
    unknownTokens: [...used].filter((name) => !(name in variables)),
    unused: Object.keys(variables).filter((name) => !used.has(name)),
    noMetric: metricKeysOf(variables).length === 0,
    empty: content.trim() === "",
    tooLong: content.length > maxLength,
  };
}

export function hasDraftIssues(issues: SmartTextDraftIssues): boolean {
  return (
    issues.malformedTokens.length > 0 ||
    issues.misplacedTokens ||
    issues.unknownTokens.length > 0 ||
    issues.unused.length > 0 ||
    issues.noMetric ||
    issues.empty ||
    issues.tooLong
  );
}

/** A name for a new variable: the metric key (or "period") made into a
 *  valid name, with a suffix per kind, unique among `taken`. */
export function suggestVariableName(
  variable: SmartTextVariable,
  taken: Iterable<string>,
): string {
  const used = new Set(taken);
  const slug = (key: string) => {
    let s = key.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "");
    if (!/^[a-z]/.test(s)) s = `v_${s}`.replace(/_+$/, "");
    return s || "value";
  };
  let base: string;
  switch (variable.kind) {
    case "value":
      base = slug(variable.metric_key);
      break;
    case "ratio":
      base = `${slug(variable.metric_key)}_share`;
      break;
    case "delta":
      base = `${slug(variable.metric_key)}_${variable.compare === "same_period_last_year" ? "vs_ly" : "vs_prev"}${
        variable.as === "percent" ? "_pct" : ""
      }`;
      break;
    case "period":
      base = "period";
      break;
  }
  // Room for a "_12" suffix inside the 32-character cap.
  base = base.slice(0, 29).replace(/_+$/, "") || "value";
  if (!used.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

// ---------------------------------------------------------------------------
// The refresh's answer (`RefreshItemResponse.smart_text`, S4)
// ---------------------------------------------------------------------------

export type SmartTextPeriod = {
  key: string;
  name: string;
  name_mid: string;
  range_label: string;
  start_date: string;
  end_date: string;
  complete: boolean;
};

export type SmartTextAnswer = {
  variables: Record<string, ResolvedSmartTextVariable>;
  period: SmartTextPeriod | null;
  /** "unresolved": the server could not fill in the numbers this time. */
  error: "unresolved" | null;
};

const STATUSES: readonly SmartTextStatus[] = [
  "ok",
  "no_value",
  "no_comparison",
  "partial_comparison",
  "comparison_failed",
];

/** The response's `smart_text`, read defensively: an entry that is not
 *  `{text: string, status: <known>}` is left out (its token then reads as
 *  pending, the same rule `smartTextParagraphs` applies). Null when the
 *  response carries no smart text at all (an older backend). */
export function parseSmartTextAnswer(raw: unknown): SmartTextAnswer | null {
  const record = asRecord(raw);
  if (!record) return null;
  const variables: Record<string, ResolvedSmartTextVariable> = {};
  const rawVariables = asRecord(record.variables) ?? {};
  for (const [name, value] of Object.entries(rawVariables)) {
    const entry = asRecord(value);
    if (!entry || typeof entry.text !== "string") continue;
    if (!(STATUSES as readonly unknown[]).includes(entry.status)) continue;
    variables[name] = {
      text: entry.text,
      status: entry.status as SmartTextStatus,
      ...(typeof entry.fingerprint === "string" ? { fingerprint: entry.fingerprint } : {}),
    };
  }
  const period = asRecord(record.period);
  return {
    variables,
    period:
      period && typeof period.name === "string"
        ? {
            key: String(period.key ?? ""),
            name: period.name,
            name_mid: typeof period.name_mid === "string" ? period.name_mid : period.name,
            range_label: typeof period.range_label === "string" ? period.range_label : "",
            start_date: String(period.start_date ?? ""),
            end_date: String(period.end_date ?? ""),
            complete: period.complete === true,
          }
        : null,
    error: record.error === "unresolved" ? "unresolved" : null,
  };
}

/**
 * The resolved values that still belong to what is on screen: an entry is
 * kept only when the variable's CURRENT definition is the one it was resolved
 * for (`resolvedFor`, captured when the refresh was requested). A redefined or
 * just-added variable is left out, so its token reads as pending until the
 * next refresh answers for the new meaning — never a stale value under a new
 * label. The server's sha `fingerprint` is deliberately not recomputed here.
 */
export function resolvedForDefinitions(
  answer: Pick<SmartTextAnswer, "variables"> | null,
  resolvedFor: SmartTextVariables | null,
  current: SmartTextVariables,
): Record<string, ResolvedSmartTextVariable> {
  const out: Record<string, ResolvedSmartTextVariable> = {};
  if (!answer || !resolvedFor) return out;
  for (const [name, value] of Object.entries(answer.variables)) {
    if (sameDefinition(resolvedFor[name], current[name])) out[name] = value;
  }
  return out;
}

/** True when some variable the text uses has no value for its current
 *  definition: the tile then asks for a refresh. */
export function needsResolving(
  content: string,
  current: SmartTextVariables,
  resolved: Record<string, ResolvedSmartTextVariable>,
): boolean {
  return tokenNames(content).some((name) => name in current && !(name in resolved));
}
